/**
 * CSC custom-PDP add-to-cart helper.
 *
 * Replaces the per-PDP fetch + `window.location = '/cart'` pattern with:
 *   1. Disable + "Adding…" while POST is in flight
 *   2. POST /cart/add.js with optional bundle variant (from active delivery chip)
 *   3. On success: refresh Dawn cart-drawer + cart-icon-bubble via renderContents,
 *      open the drawer, restore the button label
 *   4. On failure: re-enable the button and show an inline red error below the form
 *
 * Wire-up (called from each PDP's {% javascript %} block):
 *   window.CscPdpCart && window.CscPdpCart.attach({
 *     root: document.querySelector('.<scope>-pdp'),
 *     form: '[data-<scope>-form]',
 *     deliveryGroup: '[data-<scope>-delivery]'   // optional
 *   });
 *
 * Chips can carry the bundle variant id as either `data-bundle-variant`
 * (singular) or `data-bundle-variants` (plural — legacy). Both supported.
 */
(function () {
  if (window.CscPdpCart) return;

  function getBundleVariantId(chip) {
    if (!chip) return null;
    return chip.getAttribute('data-bundle-variant') || chip.getAttribute('data-bundle-variants');
  }

  function getCartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  // Errors we are happy to show a customer: ones we wrote, plus Shopify's own
  // line-item messages ("You can only add 2 of that to your cart"). Anything
  // untagged is an internal fault and must NOT be printed under Add To Basket —
  // see the catch handler below.
  function userError(message) {
    var err = new Error(message);
    err.cscUserFacing = true;
    return err;
  }

  function ensureErrorEl(form) {
    var existing = form.parentNode.querySelector('.csc-cart-error');
    if (existing) return existing;
    var el = document.createElement('p');
    el.className = 'csc-cart-error';
    el.setAttribute('role', 'alert');
    el.style.cssText = [
      'display:none',
      'margin:12px 0 0',
      'padding:10px 14px',
      'background:#fff0f0',
      'border-left:3px solid var(--csc-red, #c8102e)',
      'color:var(--csc-red, #c8102e)',
      'font-family:var(--mono, monospace)',
      'font-size:12px',
      'line-height:1.45',
      'letter-spacing:0.04em'
    ].join(';');
    form.parentNode.insertBefore(el, form.nextSibling);
    return el;
  }

  function readQty(form) {
    var qtyInput = form.querySelector('input[name="quantity"]');
    var n = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function humanizePropertyKey(raw) {
    return raw
      .split('-')
      .filter(Boolean)
      .map(function (part) { return part.charAt(0).toUpperCase() + part.slice(1); })
      .join(' ');
  }

  function hasProperties(properties) {
    for (var key in properties) {
      if (Object.prototype.hasOwnProperty.call(properties, key)) return true;
    }
    return false;
  }

  function readFormProperties(form) {
    var properties = {};
    form.querySelectorAll('[name^="properties["]').forEach(function (input) {
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
      var match = input.name.match(/^properties\[(.+)\]$/);
      if (!match || !input.value) return;
      properties[match[1]] = input.value;
    });
    return properties;
  }

  function readCartAttributes(form) {
    var attributes = {};
    form.querySelectorAll('[name^="attributes["]').forEach(function (input) {
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
      var match = input.name.match(/^attributes\[(.+)\]$/);
      if (!match || !input.value) return;
      attributes[match[1]] = input.value;
    });
    return attributes;
  }

  function readDataProperties(el, prefix) {
    var properties = {};
    if (!el || !el.attributes) return properties;
    Array.prototype.forEach.call(el.attributes, function (attr) {
      if (attr.name.indexOf(prefix) !== 0 || !attr.value) return;
      properties[humanizePropertyKey(attr.name.slice(prefix.length))] = attr.value;
    });
    return properties;
  }

  function mergeProperties(target, source) {
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
    return target;
  }

  function setButtonState(btn, label) {
    if (!btn) return;
    btn.disabled = !!label;
    if (!btn._cscOriginal) btn._cscOriginal = (btn.querySelector('span') || btn).textContent.trim() || 'Add To Basket';
    var span = btn.querySelector('span');
    if (span) span.textContent = label || btn._cscOriginal;
    else btn.textContent = label || btn._cscOriginal;
  }

  function attach(opts) {
    if (!opts || !opts.root) return;
    var form = typeof opts.form === 'string' ? opts.root.querySelector(opts.form) : opts.form;
    if (!form) return;
    if (form._cscBound) return;
    form._cscBound = true;

    var deliveryGroup = null;
    if (opts.deliveryGroup) {
      deliveryGroup = typeof opts.deliveryGroup === 'string' ? opts.root.querySelector(opts.deliveryGroup) : opts.deliveryGroup;
    }
    var submitBtn = form.querySelector('button[type="submit"]');
    var errorEl = ensureErrorEl(form);

    // Bottom CTA-banner "Add To Basket" buttons (and any other element marked
     // with [data-csc-trigger-submit] inside `root`) trigger the same submit
     // flow as the main form's primary button. Cleaner than anchor-to-#top
     // and means a second add-to-cart point further down the PDP actually
     // adds to cart instead of just scrolling up.
    opts.root.querySelectorAll('[data-csc-trigger-submit]').forEach(function (trigger) {
      if (trigger._cscTriggerBound) return;
      trigger._cscTriggerBound = true;
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      });
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitBtn && submitBtn.disabled) return;
      errorEl.style.display = 'none';
      errorEl.textContent = '';

      // Build the items list — main product + optional bundle variant from active chip
      var idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;
      var activeChip = deliveryGroup ? deliveryGroup.querySelector('.chip.active') : null;
      var mainProperties = mergeProperties(readFormProperties(form), readDataProperties(activeChip, 'data-line-property-'));
      var mainItem = { id: parseInt(idInput.value, 10), quantity: readQty(form) };
      if (hasProperties(mainProperties)) mainItem.properties = mainProperties;
      var items = [mainItem];

      if (deliveryGroup) {
        var bundleId = getBundleVariantId(activeChip);
        if (bundleId) {
          var bundleItem = { id: parseInt(bundleId, 10), quantity: 1 };
          var bundleProperties = readDataProperties(activeChip, 'data-bundle-property-');
          if (hasProperties(bundleProperties)) bundleItem.properties = bundleProperties;
          items.push(bundleItem);
        }
      }

      var cartEl = getCartElement();
      var sections = null;
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        sections = cartEl.getSectionsToRender().map(function (s) { return s.id; }).join(',');
      }

      var cartAttributes = readCartAttributes(form);
      var body = { items: items };
      if (sections) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      setButtonState(submitBtn, 'Adding…');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          return res.text().then(function (txt) {
            // /cart/add.js returns Content-Type: text/javascript (Shopify convention
            // because the URL ends in .js), even though the body is JSON. So we can't
            // pre-filter on content-type — trust JSON.parse as the source of truth.
            // Truly-non-JSON responses (Cloudflare HTML challenge, login wall) will
            // throw here and get the same friendly error.
            try {
              var data = JSON.parse(txt);
              return { ok: res.ok, data: data };
            } catch (e) {
              throw userError('We couldn’t reach the basket right now. Please try again, or refresh the page if the issue persists.');
            }
          });
        })
        .then(function (r) {
          if (!r.ok) {
            var msg = (r.data && (r.data.description || r.data.message)) || 'Sorry — something went wrong. Please try again.';
            throw userError(msg);
          }
          if (hasProperties(cartAttributes)) {
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ attributes: cartAttributes })
            }).then(function (attrRes) {
              if (!attrRes.ok) throw userError('Pre-order details could not be saved to the basket. Please try again.');
              return r;
            });
          }
          return r;
        })
        .then(function (r) {

          // Refresh the cart UI in place
          if (cartEl && typeof cartEl.renderContents === 'function') {
            if (typeof cartEl.setActiveElement === 'function') {
              cartEl.setActiveElement(document.activeElement);
            }
            cartEl.renderContents(r.data); // also opens the drawer
          } else {
            // No Dawn cart element on this page — fall back to redirecting
            window.location.href = '/cart';
            return;
          }

          // Publish Dawn's pub-sub event so other components (mini-cart, etc.) refresh
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'csc-pdp-cart', productVariantId: idInput.value });
          }

          // Screen-reader announcement (Goal C)
          if (window.cscCartFeedback) {
            window.cscCartFeedback.announce(window.cscCartFeedback.titleFromResponse(r.data));
          }

          setButtonState(submitBtn, null); // restore original label
        })
        .catch(function (err) {
          setButtonState(submitBtn, null);
          // Never print an internal fault to a customer. Before this guard, a
          // TypeError thrown inside the cart drawer surfaced here verbatim as
          // "null is not an object (evaluating 'parsedState.sections[section.id]')"
          // in the red box under Add To Basket — the item WAS in the basket, but
          // the customer read it as a failed order and left.
          if (err && err.cscUserFacing && err.message) {
            errorEl.textContent = err.message;
          } else {
            if (window.console && console.error) console.error('[csc-pdp-cart]', err);
            errorEl.textContent = 'Sorry — we couldn’t add that to your basket. Please try again, or call 01293 824 094.';
          }
          errorEl.style.display = 'block';
        });
    });
  }

  window.CscPdpCart = { attach: attach };
})();
